import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { BsFileImage } from 'react-icons/bs';
import { FiUploadCloud, FiDownload, FiCrop } from 'react-icons/fi';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import SEO from '../components/SEO';
import { imageCropperSchemas } from '../schemas';
import './ImageCropper.css';

const MAX_FILE_SIZE = 20 * 1024 * 1024;

const ImageCropper = () => {
  const fileInputRef = useRef(null);
  const imageRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [fileName, setFileName] = useState('');
  const [crop, setCrop] = useState({
    unit: '%',
    width: 50,
    height: 50,
    x: 25,
    y: 25
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [croppedImageUrl, setCroppedImageUrl] = useState(null);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [cropValues, setCropValues] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0
  });

  const handleFileSelect = (file) => {
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }
    
    if (file.size > MAX_FILE_SIZE) {
      setError('Image size should be less than 20 MB.');
      return;
    }

    setError(null);
    setFileName(file.name);
    setCroppedImageUrl(null);

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleCropComplete = (crop) => {
    setCompletedCrop(crop);
    if (imageRef.current && crop.width && crop.height) {
      setCropValues({
        x: Math.round(crop.x),
        y: Math.round(crop.y),
        width: Math.round(crop.width),
        height: Math.round(crop.height)
      });
    }
  };

  const handleManualCropChange = (field, value) => {
    const numValue = parseInt(value) || 0;
    setCropValues(prev => ({ ...prev, [field]: numValue }));
    
    if (imageRef.current) {
      const image = imageRef.current;
      setCrop(prevCrop => ({
        ...prevCrop,
        [field]: numValue,
        unit: 'px'
      }));
    }
  };

  const generateCroppedImage = () => {
    if (!completedCrop || !imageRef.current) return;

    setIsSaving(true);

    const image = imageRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = Math.max(1, Math.round(completedCrop.width));
    canvas.height = Math.max(1, Math.round(completedCrop.height));
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    const startedAt = Date.now();
    const elapsed = Date.now() - startedAt;
    const remainingWait = Math.max(0, 3000 - elapsed);

    setTimeout(() => {
      canvas.toBlob((blob) => {
        if (blob) {
          if (croppedImageUrl) {
            URL.revokeObjectURL(croppedImageUrl);
          }
          const url = URL.createObjectURL(blob);
          setCroppedImageUrl(url);
        }
        setIsSaving(false);
      }, 'image/png');
    }, remainingWait);
  };

  const downloadCroppedImage = () => {
    if (!croppedImageUrl) return;
    
    const link = document.createElement('a');
    link.href = croppedImageUrl;
    link.download = `cropped-${fileName}`;
    link.click();
  };

  const reset = () => {
    if (croppedImageUrl) {
      URL.revokeObjectURL(croppedImageUrl);
    }

    setImageSrc(null);
    setFileName('');
    setCrop({ unit: '%', width: 50, height: 50, x: 25, y: 25 });
    setCompletedCrop(null);
    setCroppedImageUrl(null);
    setIsSaving(false);
    setCropValues({ x: 0, y: 0, width: 0, height: 0 });
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="cropper-page">
      <SEO 
        title="Image Cropper Online – Crop Photos Free & Fast"
        description="Crop images online free with precise control. Resize and crop JPG, PNG & WEBP photos for social media, profile pics & more."
        keywords="image cropper, crop photo online, crop image free"
        path="/image-cropper"
        schema={imageCropperSchemas}
      />
      <div className="cropper-container">
        <header className="cropper-header">
          <h1 className="cropper-title">Image Cropper</h1>
          <p className="cropper-subtitle">Crop and resize your images with precision.</p>
        </header>

        {error && (
          <div className="error-message">
            <div className="error-content">
              <span className="error-icon">!</span>
              <p className="error-text">{error}</p>
              <button className="error-close" onClick={() => setError(null)}>×</button>
            </div>
          </div>
        )}

        <div className="cropper-content-wrapper">
          {/* Left Decorative Image */}
          <img 
            src="/img-1.png" 
            alt="Decorative" 
            className="decorative-img decorative-img-left"
          />

          {/* Right Decorative Image */}
          <img 
            src="/img-2.png" 
            alt="Decorative" 
            className="decorative-img decorative-img-right"
          />

          <div className="cropper-main-content">
            {!imageSrc ? (
              <motion.div
                className={`upload-area ${isDragging ? 'dragging' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="upload-content">
                  <div className="upload-icon-wrapper">
                    <FiUploadCloud className="upload-icon" />
                  </div>
                  <h2 className="upload-title">Upload an image</h2>
                  <p className="upload-description">
                    Drag and drop an image here, or click to select
                  </p>
                  <button
                    type="button"
                    className="upload-button"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <BsFileImage /> Choose image
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileSelect(e.target.files[0])}
                    hidden
                  />
                </div>
                <div className="upload-glow" />
              </motion.div>
            ) : (
              <div className="crop-container">
                {isSaving ? (
                  <div className="save-preview-wrapper">
                    <div className="save-preview-card">
                      <div className="save-preview-content">
                        <div className="save-loader-wrap">
                          <span className="save-loader" />
                        </div>
                        <div className="save-preview-text">
                          <span className="save-preview-label">Saving your crop...</span>
                          <span className="save-preview-subtext">Preparing your final image</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : croppedImageUrl ? (
                  <div className="crop-preview-wrapper">
                    <div className="crop-preview-card">
                      <div className="crop-preview-top">
                        <span className="crop-preview-title">Crop Preview</span>
                        <span className="crop-preview-file">{fileName}</span>
                      </div>

                      <div className="crop-preview-image-wrap">
                        <img src={croppedImageUrl} alt="Cropped preview" className="crop-preview-image" />
                      </div>

                      <div className="crop-preview-actions">
                        <button className="download-button" onClick={downloadCroppedImage}>
                          <FiDownload /> Download Now
                        </button>
                        <button className="reset-button alt-reset" onClick={reset}>
                          Choose another image
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="crop-card">
                    <div className="crop-header">
                      <FiCrop className="crop-icon" />
                      <span className="crop-filename">{fileName}</span>
                    </div>

                    <div className="crop-image-wrapper">
                      <ReactCrop
                        crop={crop}
                        onChange={(c) => setCrop(c)}
                        onComplete={handleCropComplete}
                        aspect={undefined}
                      >
                        <img
                          ref={imageRef}
                          src={imageSrc}
                          alt="Crop preview"
                          className="crop-image"
                        />
                      </ReactCrop>
                    </div>

                    <div className="crop-controls">
                      <h3 className="controls-title">Crop Dimensions (px)</h3>
                      <div className="crop-inputs-grid">
                        <div className="crop-input-group">
                          <label className="input-label">X Position</label>
                          <input
                            type="number"
                            className="crop-input"
                            value={cropValues.x}
                            onChange={(e) => handleManualCropChange('x', e.target.value)}
                            min="0"
                          />
                        </div>
                        <div className="crop-input-group">
                          <label className="input-label">Y Position</label>
                          <input
                            type="number"
                            className="crop-input"
                            value={cropValues.y}
                            onChange={(e) => handleManualCropChange('y', e.target.value)}
                            min="0"
                          />
                        </div>
                        <div className="crop-input-group">
                          <label className="input-label">Width</label>
                          <input
                            type="number"
                            className="crop-input"
                            value={cropValues.width}
                            onChange={(e) => handleManualCropChange('width', e.target.value)}
                            min="0"
                          />
                        </div>
                        <div className="crop-input-group">
                          <label className="input-label">Height</label>
                          <input
                            type="number"
                            className="crop-input"
                            value={cropValues.height}
                            onChange={(e) => handleManualCropChange('height', e.target.value)}
                            min="0"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="cropper-action-spaced">
                      <button
                        className="save-button"
                        onClick={generateCroppedImage}
                        disabled={!completedCrop?.width || !completedCrop?.height}
                      >
                        <FiCrop /> Save Crop
                      </button>

                      <button className="reset-button" onClick={reset}>
                        Choose another image
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
